import type { FC } from "react";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import {
  type ResolvedTool,
  useToolCatalog,
} from "~/pages/manifold-nextgen/components/conversations/useToolCatalog";

export interface SubagentEvent {
  subagent: string;
  toolCallId: string;
  kind: "tool_call" | "tool_result";
  toolName: string;
  summary?: string;
}

interface SubagentActivityProps {
  events: SubagentEvent[];
}

// A result is an error only when the summary explicitly says so.
// Everything else (JSON data, skill content, etc.) counts as success.
function isError(summary: string | undefined): boolean {
  if (!summary) {
    return false;
  }
  const s = summary.toLowerCase();
  return (
    s.startsWith("error") ||
    s.startsWith("there was an error") ||
    s.startsWith("failed") ||
    s.startsWith("traceback")
  );
}

function getStepLabel(evt: SubagentEvent, resolveTool: (name: string) => ResolvedTool): string {
  if (evt.kind === "tool_call") {
    return resolveTool(evt.toolName).displayName;
  }
  return evt.summary?.split("\n")[0] ?? "Done";
}

/**
 * Pair each tool_call with its tool_result by arrival order (FIFO).
 * All subagent events share the same parent toolCallId, so we can't
 * match by ID — positional pairing is the only reliable strategy.
 * Returns a map of tool_call event index → its paired tool_result event.
 */
function buildCallResultPairs(events: SubagentEvent[]): Map<number, SubagentEvent> {
  const pairs = new Map<number, SubagentEvent>();
  const unresolvedCallIndices: number[] = [];

  events.forEach((evt, i) => {
    if (evt.kind === "tool_call") {
      unresolvedCallIndices.push(i);
    } else if (evt.kind === "tool_result" && unresolvedCallIndices.length > 0) {
      pairs.set(unresolvedCallIndices.shift()!, evt);
    }
  });

  return pairs;
}

export const SubagentActivity: FC<SubagentActivityProps> = ({ events }) => {
  const { resolveTool } = useToolCatalog();
  if (events.length === 0) {
    return null;
  }

  const callResultPairs = buildCallResultPairs(events);

  return (
    <div className="mb-2 space-y-1 border-l-2 border-gray-200 pl-2.5">
      {events.map((evt, i) => (
        <div key={i} className="flex items-start gap-1.5 text-xs text-gray-500">
          <StepIcon evt={evt} resolvedResult={callResultPairs.get(i)} />
          <span className="min-w-0 break-words leading-relaxed">
            {getStepLabel(evt, resolveTool)}
          </span>
        </div>
      ))}
    </div>
  );
};

const StepIcon: FC<{ evt: SubagentEvent; resolvedResult?: SubagentEvent }> = ({
  evt,
  resolvedResult,
}) => {
  if (evt.kind === "tool_call") {
    if (!resolvedResult) {
      // Still in-flight
      return <Loader2 className="mt-0.5 size-3 shrink-0 animate-spin text-gray-400" />;
    }
    return isError(resolvedResult.summary) ? (
      <XCircle className="mt-0.5 size-3 shrink-0 text-red-400" />
    ) : (
      <CheckCircle2 className="mt-0.5 size-3 shrink-0 text-green-500" />
    );
  }
  return isError(evt.summary) ? (
    <XCircle className="mt-0.5 size-3 shrink-0 text-red-400" />
  ) : (
    <CheckCircle2 className="mt-0.5 size-3 shrink-0 text-green-500" />
  );
};
